"use client"

import { useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X } from "lucide-react"

const VideoModal = ({ isOpen, onClose, videoUrl }) => {
  // Extract video ID from YouTube URL
  const getYouTubeId = (url) => {
    const match = url?.match(/[?&]v=([^&]+)/)
    return match ? match[1] : null
  }

  const videoId = getYouTubeId(videoUrl)

  // Close on escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.keyCode === 27) onClose()
    }
    if (isOpen) {
      document.addEventListener("keydown", handleEsc)
      document.body.style.overflow = "hidden"
    }
    return () => {
      document.removeEventListener("keydown", handleEsc)
      document.body.style.overflow = "unset"
    }
  }, [isOpen, onClose])

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="relative w-full max-w-5xl pointer-events-auto">
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute -top-12 right-0 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors group"
                aria-label="Close video"
              >
                <X className="h-6 w-6 text-white group-hover:rotate-90 transition-transform" />
              </button>
              
              {/* Video container */}
              <div className="relative rounded-2xl shadow-2xl overflow-hidden">
                <div className="relative bg-black rounded-2xl overflow-hidden aspect-video">
                  {videoId ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
                      title="AVA Demo Video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-white">
                      Video unavailable
                    </div>
                  )}
                </div>
              </div>
              
              {/* Title below video */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-center mt-4"
              >
                <h3 className="text-white text-lg font-semibold">
                  See AVA in Action
                </h3>
                <p className="text-white/70 text-sm mt-1">
                  Your AI-Powered Productivity Assistant
                </p>
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default VideoModal